//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { Box, Chip, useTheme } from '@mui/material'
import { GridColDef, GridRowsProp } from '@mui/x-data-grid-premium'
import { EthAddr } from '@shared/SchemaTypes'
import React, { ReactElement, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import {
  DataGridPremium,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarFilterButton,
} from '@mui/x-data-grid-premium'

import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import { RoleEnum } from '@shared/Enums'

import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined'

import { useOtChainState } from '@react/redux/store'

import { getPoolAssetCSV, uploadAssetsCsv } from '@react/apx/AwsApx'
import { logOT } from '@shared/loggingShared'

function CustomToolbar(): ReactElement {
  return (
    <GridToolbarContainer style={{ float: 'right', padding: '12px', borderBottom: '1px solid #E0E0E0' }}>
      <GridToolbarFilterButton
        slotProps={{
          button: {
            startIcon: <FilterAltOutlinedIcon />,
            sx: {
              color: '#171717',
              paddingLeft: '8px',
              paddingRight: '8px',
              textTransform: 'none',
              backgroundColor: '#EEEEEE',
              '&:hover': {
                backgroundColor: '#F5F5F5',
              },
            },
          },
        }}
      />
      <GridToolbarColumnsButton
        slotProps={{
          button: {
            startIcon: <ViewColumnOutlinedIcon />,
            sx: {
              marginLeft: '4px',
              paddingLeft: '8px',
              paddingRight: '8px',
              textTransform: 'none',
              marginRight: '4px',
              color: '#171717',
              backgroundColor: '#EEEEEE',
              '&:hover': {
                backgroundColor: '#F5F5F5',
              },
            },
          },
        }}
      />
      <GridToolbarExport
        slotProps={{
          button: {
            sx: {
              color: '#171717',
              paddingLeft: '8px',
              paddingRight: '8px',
              textTransform: 'none',
              backgroundColor: '#EEEEEE',
              '&:hover': {
                backgroundColor: '#F5F5F5',
              },
            },
          },
        }}
      />
    </GridToolbarContainer>
  )
}

export default function PoolAssets(props: { poolAddr?: EthAddr }): ReactElement {
  const poolAddr = (props.poolAddr ? props.poolAddr : useParams().poolAddr) as EthAddr

  const {
    cognitoUser,
    stageConfiguration: { stageName, restAPIKey },
    userConnection: { roleEnum },
    chainConfiguration: {
      chainId,

      perimeterAddresses,
    },
  } = useOtChainState()

  const [columns, setColumns] = useState<GridColDef[]>([])
  const theme = useTheme()
  const [rows, setRows] = useState<GridRowsProp>([])
  const [fileListId, setFileListId] = React.useState<string>('1')

  const handleCSVSelect = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0]
    if (file && poolAddr && stageName && cognitoUser) {
      uploadAssetsCsv(stageName, poolAddr, file, restAPIKey, cognitoUser)
        .then(() => {
          setFileListId(Math.random().toString())
        })
        .catch((e) => {
          console.error(e)
        })
    }
  }

  useEffect(() => {
    if (poolAddr && stageName) {
      getPoolAssetCSV(stageName, poolAddr)
        .then((csv: { columns: GridColDef[]; rows: GridRowsProp } | undefined) => {
          if (csv) {
            setColumns(csv.columns)
            setRows(csv.rows)
          }
        })
        .catch((_e) => {})
    }
  }, [chainId, perimeterAddresses, poolAddr, fileListId])
  logOT.debug('Data grid', columns, rows)
  logOT.info('FOCUS', roleEnum, RoleEnum.POOL_ADMIN, RoleEnum.BORROWER)
  return (
    <Box px={3} py={2} sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {(roleEnum === RoleEnum.POOL_ADMIN || roleEnum === RoleEnum.BORROWER) && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
          <input
            id="contained-CSV-file"
            style={{ width: 0 }}
            type="file"
            accept="text/csv"
            onChange={handleCSVSelect}
          />
          <label htmlFor="contained-CSV-file">
            <Chip
              sx={{
                height: '40px',
                width: '150px',
                backgroundColor: theme.palette.fg.main,
                color: '#FFFFFF',
                padding: '11px 16px 11px 16px',
                borderRadius: '6px',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: theme.palette.fg.dark,
                },
              }}
              icon={<UploadFileIcon />}
              label={'Upload'}
            />
          </label>
        </Box>
      )}

      {columns?.length > 0 && rows?.length > 0 && (
        <DataGridPremium
          sx={{ fontSize: 12, fontWeight: 500 }}
          autoHeight
          rows={rows}
          columns={columns}
          slots={{
            toolbar: CustomToolbar,
            openFilterButtonIcon: FilterAltOutlinedIcon,
          }}
        />
      )}
    </Box>
  )
}
